import React, {useEffect,useRef,useState} from 'react';
import {preparationSnapshot,speechHash} from '../core/preparedSpeech.js';
import {preparationMessage} from '../core/preparationMessage.js';
import {libraryKey,libraryRequest} from '../core/preparedLibrary.js';
import {sessionApiUrl} from '../realtime/sessionEndpoint.js';
import {readBoundedArrayBuffer} from '../../cloudflare/requestBody.js';

export function PreparedSpeech({scenario,identity,rehearsal=false,active=true,connected=true,revision=0,allowGeneration=false}) {
  const [status,setStatus]=useState({state:'needed'}),[busy,setBusy]=useState(false),[notice,setNotice]=useState('');
  const owner=useRef({epoch:0,controller:null,alive:true});
  const scenarioKey=JSON.stringify(scenario);
  const held=useRef({scenario,identity,rehearsal,allowGeneration});held.current={scenario,identity,rehearsal,allowGeneration};
  const current=(epoch)=>owner.current.alive && owner.current.epoch===epoch;
  async function request(action,extra={},retryBusy=false) {
    const value=held.current;
    if(!value.identity?.roomCode || !value.identity?.token) {setNotice('Open your Director room to check its private audio.');return false;}
    if(['start','step'].includes(action) && !value.allowGeneration) {setNotice('Missing recordings still need completion. Saved audio can be checked without recording again.');return false;}
    const epoch=++owner.current.epoch,controller=new AbortController();
    owner.current.controller?.abort();owner.current.controller=controller;setBusy(true);
    setStatus({state:action==='status'?'checking':'working'});setNotice('');
    try {
      const snapshot=preparationSnapshot(value.scenario),inputRevision=await speechHash(snapshot);
      for(let attempt=0;attempt<4;attempt++) {
        if(!current(epoch))return false;
        const route=value.rehearsal?'rehearsal-preparation':'speech-preparation';
        const response=await fetch(sessionApiUrl('/api/rooms/'+encodeURIComponent(value.identity.roomCode)+'/'+route+'/'+action),{
          method:'POST',headers:{'content-type':'application/json',authorization:'Bearer '+value.identity.token},
          body:JSON.stringify(action==='start'?snapshot:{revision:inputRevision,...extra}),signal:AbortSignal.any([controller.signal,AbortSignal.timeout(90000)]),cache:'no-store',redirect:'error'});
        const bytes=await readBoundedArrayBuffer(response,4*1024*1024,'Saved audio response is too large.',controller.signal);
        const data=JSON.parse(new TextDecoder().decode(bytes));
        if(!current(epoch))return false;
        if(response.status===429 && retryBusy && attempt<3) {await new Promise(resolve=>setTimeout(resolve,500*(attempt+1)));continue;}
        if(!response.ok)throw Error(data.message || 'Saved audio could not be checked. Retry when the service returns.');
        if(!data || typeof data.state!=='string')throw Error('Saved audio status was invalid.');
        setStatus(data);setNotice(preparationMessage(data.message).text);return data;
      }
      return false;
    }catch(error){if(current(epoch)){setStatus({state:'failed'});setNotice(error.message);}return false;}
    finally{if(current(epoch))setBusy(false);}
  }
  useEffect(()=>{
    owner.current.alive=true;
    if(active && connected)void request('status',{},true);
    return()=>{owner.current.alive=false;owner.current.epoch++;owner.current.controller?.abort();};
  },[active,connected,identity?.roomCode,identity?.token,scenarioKey,revision]);
  async function update() {
    const old=status.revision;
    if(old && status.state==='stale' && !await request('stop',{revision:old}))return;
    return request('start');
  }
  async function save() {
    if(status.state!=='ready' || busy)return;
    const epoch=owner.current.epoch,controller=new AbortController(),value=held.current;owner.current.controller=controller;setBusy(true);
    try{
      await libraryRequest('save',{scope:'private'},{key:libraryKey(true),room:value.identity,signal:controller.signal});
      if(current(epoch))setNotice('Saved privately. Keep your library access backup.');
    }catch(error){if(current(epoch))setNotice(error.message);}
    finally{if(current(epoch))setBusy(false);}
  }
  const ready=status.state==='ready';
  return <article className="surface prepared-speech" data-preparation-state={status.state} hidden={!active}>
    <h2>{ready?'Scene audio is ready':'Check saved scene audio'}</h2>
    <p>Saved recordings are checked for this script. Unrecorded items can use private captions when each actor selects recordings and captions. Fully spoken rehearsal still needs those recordings.</p>
    {notice && <p role="status">{notice}</p>}
    {Number.isInteger(status.total) && <p>{status.ready || 0} of {status.total} audio items available.</p>}
    <button type="button" disabled={busy || !connected} onClick={()=>request('status',{},true)}>Retry saved-audio check</button>
    <button type="button" disabled={busy || !connected || !allowGeneration} onClick={()=>request('start')}>Prepare dialogue</button>
    {status.state==='stale' && <button type="button" disabled={busy || !allowGeneration} onClick={update}>Update scene audio</button>}
    <button type="button" disabled={busy || !allowGeneration} onClick={()=>request('start')}>Retry preparation</button>
    <button type="button" disabled={busy || !connected} onClick={()=>request('stop',{revision:status.revision})}>Stop preparation</button>
    <button type="button" disabled={!ready || busy} onClick={save}>Save to my private library</button>
  </article>;
}
